/**
 * 大会順位計算
 */
import { TournamentState, TournamentParticipant } from '../types/Tournament';

/**
 * 総当たり戦の順位を計算する。
 * ソート基準: 勝ち点降順 → 勝利数降順 → ID昇順
 *
 * @param state 大会の状態
 * @returns 順位順にソートされた参加者リスト
 */
export function calculateRoundRobinStandings(state: TournamentState): TournamentParticipant[] {
  return [...state.participants].sort((a, b) => {
    if (b.points !== a.points) return b.points - a.points;
    if (b.wins !== a.wins) return b.wins - a.wins;
    return a.id.localeCompare(b.id);
  });
}

/**
 * トーナメント戦の順位を計算する。
 * 到達ラウンドが高い参加者が上位。同ラウンド敗退者はID昇順。
 *
 * @param state 大会の状態
 * @returns 順位順にソートされた参加者リスト
 */
export function calculateEliminationStandings(state: TournamentState): TournamentParticipant[] {
  // 各参加者の最高到達ラウンドを算出
  const maxRound = new Map<string, number>();
  state.participants.forEach((p) => maxRound.set(p.id, -1));

  for (const match of state.matches) {
    if (!match.result || match.round === undefined) continue;

    const { winnerId, loserId } = match.result;

    // 敗者のラウンドを記録（敗退したラウンド）
    if (loserId) {
      const current = maxRound.get(loserId) ?? -1;
      if (match.round > current) {
        maxRound.set(loserId, match.round);
      }
    }

    // 勝者はこのラウンド+1に到達（決勝勝者は特別扱い）
    if (winnerId) {
      const current = maxRound.get(winnerId) ?? -1;
      const reachedRound = match.round + 1;
      if (reachedRound > current) {
        maxRound.set(winnerId, reachedRound);
      }
    }
  }

  return [...state.participants].sort((a, b) => {
    const roundA = maxRound.get(a.id) ?? -1;
    const roundB = maxRound.get(b.id) ?? -1;
    if (roundB !== roundA) return roundB - roundA;
    return a.id.localeCompare(b.id);
  });
}
